import SystemUpdateAltOutlinedIcon from '@mui/icons-material/SystemUpdateAltOutlined'
import { Alert, Button, Snackbar } from '@mui/material'
import { useRef, useState } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import styles from './PwaUpdatePrompt.module.scss'

/** How often an open app asks whether a new version has been deployed. */
export const updateCheckInterval = 60 * 60 * 1000
/** If nothing has reloaded the page this long after Update now, reload anyway. */
export const updateReloadFallback = 5000
/** Returning to the app checks too, but not more often than this. */
export const updateCheckMinimumGap = 5 * 60 * 1000

/**
 * Browsers only look for a new service worker on a full page load, and an
 * installed app can stay open for days without one. Check on a timer, when
 * the app comes back to the foreground, and when the connection returns.
 */
export function watchForUpdates(
  registration: { update: () => Promise<unknown> },
  { interval = updateCheckInterval, minimumGap = updateCheckMinimumGap, now = () => Date.now() } = {},
) {
  let lastCheck = now()
  const check = () => {
    if (!navigator.onLine || now() - lastCheck < minimumGap) return
    lastCheck = now()
    registration.update().catch(() => undefined)
  }
  const onVisible = () => { if (document.visibilityState === 'visible') check() }
  const timer = window.setInterval(check, interval)
  document.addEventListener('visibilitychange', onVisible)
  window.addEventListener('online', check)
  return () => {
    window.clearInterval(timer)
    document.removeEventListener('visibilitychange', onVisible)
    window.removeEventListener('online', check)
  }
}

/**
 * Offers a new version without forcing it on someone mid-registration. The
 * prompt stays until it is answered: tapping elsewhere on the page (filling in
 * the form, say) does not dismiss it. Accepting reloads this window; another
 * window of the app is told about the update instead of being reloaded under
 * the person using it.
 */
export function PwaUpdatePrompt({ reload = () => window.location.reload(), quiet = false }: { reload?: () => void; quiet?: boolean }) {
  const [deferred, setDeferred] = useState(false)
  const [updatedElsewhere, setUpdatedElsewhere] = useState(false)
  const [updating, setUpdating] = useState(false)
  const updatingHere = useRef(false)
  const registrationRef = useRef<ServiceWorkerRegistration | undefined>(undefined)
  const reloadRef = useRef(reload)
  const reloaded = useRef(false)
  const reloadOnce = () => {
    if (reloaded.current) return
    reloaded.current = true
    reloadRef.current()
  }
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: false,
    onRegisteredSW(_url, registration) {
      registrationRef.current = registration
      if (registration) watchForUpdates(registration)
    },
    // The new version has taken over. Reload only the window that asked for it.
    onNeedReload() {
      if (updatingHere.current) reloadOnce()
      else setUpdatedElsewhere(true)
    },
  })

  // Quiet (before signing in): keep looking for updates, but don't ask yet.
  // The prompt shows on the first screen after signing in.
  if (quiet && !updating) return null

  if (updatedElsewhere) {
    return (
      <Snackbar className={styles.snackbar} open>
        <Alert
          className={styles.raised}
          severity="info"
          role="status"
          icon={<SystemUpdateAltOutlinedIcon />}
          action={<Button className={styles.primary} variant="outlined" size="small" onClick={() => reloadRef.current()}>Reload</Button>}
        >
          Outreach OS was updated in another window. Reload when you’re ready to use the new version.
        </Alert>
      </Snackbar>
    )
  }

  return (
    <Snackbar
      className={styles.snackbar}
      // updateServiceWorker clears needRefresh as soon as it sends the
      // activation request. Keep the prompt mounted until the reload has
      // happened, otherwise the progress label vanishes immediately.
      open={updating || (needRefresh && !deferred)}
    >
      {/* The same banner as everywhere else, raised because it floats over the
          screen. Update now is outlined in ivory, never gold, so it doesn't
          compete with the primary action of the screen underneath. */}
      <Alert
        className={styles.raised}
        severity="info"
        role="status"
        icon={<SystemUpdateAltOutlinedIcon />}
        action={
          <>
            <Button
              className={`${styles.primary} ${styles.updateButton}`}
              variant="outlined"
              size="small"
              disabled={updating}
              onClick={() => {
                updatingHere.current = true
                setUpdating(true)
                const waiting = registrationRef.current?.waiting
                void updateServiceWorker(true)
                // On a page the service worker doesn't control (a first visit, or
                // after a hard refresh) nothing reports the new version taking
                // over, so Update now used to do nothing at all. There the new
                // version may already be active with nothing left waiting: just
                // reload. If it is still waiting, reload once it activates. And
                // whatever happens, reload after a few seconds.
                if (!waiting) reloadOnce()
                else if (!navigator.serviceWorker?.controller) {
                  waiting.addEventListener('statechange', () => {
                    if (waiting.state === 'activated') reloadOnce()
                  })
                }
                window.setTimeout(reloadOnce, updateReloadFallback)
              }}
            >
              {updating ? 'Updating…' : 'Update now'}
            </Button>
            <Button color="inherit" size="small" onClick={() => setDeferred(true)}>Later</Button>
          </>
        }
      >
        {updating ? 'Updating Outreach OS…' : 'A new version of Outreach OS is ready.'}
      </Alert>
    </Snackbar>
  )
}
